import { useState, useRef, useEffect } from "react";
import { Link } from "react-router";
import { Bell, CheckCheck, Settings, Loader2, Sparkles } from "lucide-react";
import { useNotifications } from "../../contexts/NotificationContext";
import { useAuth } from "../../contexts/authContext";
import NotificationItem from "./NotificationItem";

export default function NotificationBell() {
  const { user } = useAuth();
  const {
    unreadCount,
    notifications,
    isLoading,
    isFetchingMore,
    hasMore,
    loadMore,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    dismissNotification,
  } = useNotifications();

  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all"); // "all" | "unread"
  const dropdownRef = useRef(null);

  // Fetch when dropdown is opened
  useEffect(() => {
    if (isOpen) {
      fetchNotifications({
        targetPage: 1,
        append: false,
        activeFilters: activeTab === "unread" ? { is_read: false } : {},
      });
    }
  }, [isOpen, activeTab, fetchNotifications]);

  // Click outside listener
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Escape key listener
  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const displayedNotifications =
    activeTab === "unread"
      ? notifications.filter((n) => !n.is_read)
      : notifications;

  const notificationsPageLink = user?.user_type
    ? `/${user.user_type}/notifications`
    : "/notifications";

  const preferencesLink = user?.user_type
    ? `/${user.user_type}/settings`
    : "/settings";

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        type="button"
        aria-label="Notifications"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        className="relative p-2 rounded-full text-base-content/80 hover:text-base-content hover:bg-base-200 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 bg-red-500 text-white text-[11px] font-bold rounded-full flex items-center justify-center shadow-sm animate-in zoom-in-50">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-base-100 rounded-2xl shadow-2xl border border-base-200 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
          {/* Header */}
          <div className="p-3.5 border-b border-base-200 flex items-center justify-between bg-base-100/90 backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-base text-base-content">
                Notifications
              </h3>
              {unreadCount > 0 && (
                <span className="badge badge-sm badge-primary font-medium text-xs">
                  {unreadCount} unread
                </span>
              )}
            </div>

            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={() => markAllAsRead()}
                  title="Mark all as read"
                  className="btn btn-ghost btn-xs text-xs text-primary hover:bg-primary/10 gap-1 font-medium"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  <span>Mark all read</span>
                </button>
              )}
              <Link
                to={preferencesLink}
                onClick={() => setIsOpen(false)}
                title="Notification preferences"
                className="btn btn-ghost btn-xs btn-circle text-base-content/60 hover:text-base-content"
              >
                <Settings className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex px-3 pt-2 gap-1 border-b border-base-200 bg-base-200/30">
            <button
              type="button"
              onClick={() => setActiveTab("all")}
              className={`pb-2 px-3 text-xs font-medium border-b-2 transition-colors ${
                activeTab === "all"
                  ? "border-primary text-primary"
                  : "border-transparent text-base-content/60 hover:text-base-content"
              }`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("unread")}
              className={`pb-2 px-3 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                activeTab === "unread"
                  ? "border-primary text-primary"
                  : "border-transparent text-base-content/60 hover:text-base-content"
              }`}
            >
              <span>Unread</span>
              {unreadCount > 0 && (
                <span className="w-4 h-4 rounded-full bg-primary/20 text-primary text-[10px] font-bold flex items-center justify-center">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>
          </div>

          {/* List Area */}
          <div className="max-h-[380px] overflow-y-auto divide-y divide-base-200/70">
            {isLoading && notifications.length === 0 ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3].map((n) => (
                  <div key={n} className="flex gap-3 animate-pulse">
                    <div className="w-9 h-9 rounded-xl bg-base-300 shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3.5 bg-base-300 rounded w-2/3" />
                      <div className="h-3 bg-base-300 rounded w-full" />
                      <div className="h-2.5 bg-base-300 rounded w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : displayedNotifications.length === 0 ? (
              <div className="p-8 text-center flex flex-col items-center justify-center text-base-content/60">
                <div className="w-12 h-12 rounded-full bg-base-200 flex items-center justify-center mb-2 text-base-content/40">
                  <Sparkles className="w-6 h-6" />
                </div>
                <p className="text-sm font-semibold text-base-content">
                  {activeTab === "unread" ? "All caught up!" : "No notifications yet"}
                </p>
                <p className="text-xs text-base-content/60 mt-1 max-w-[200px]">
                  {activeTab === "unread"
                    ? "You have marked all your notifications as read."
                    : "We will notify you about quizzes, courses, and updates here."}
                </p>
              </div>
            ) : (
              <>
                {displayedNotifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    compact={true}
                    onMarkAsRead={markAsRead}
                    onDismiss={dismissNotification}
                    onNavigate={() => setIsOpen(false)}
                  />
                ))}

                {/* Load More inside dropdown if hasMore */}
                {hasMore && (
                  <div className="p-2 text-center bg-base-100">
                    <button
                      type="button"
                      disabled={isFetchingMore}
                      onClick={loadMore}
                      className="btn btn-ghost btn-xs text-xs text-primary w-full gap-1"
                    >
                      {isFetchingMore ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          <span>Loading more...</span>
                        </>
                      ) : (
                        <span>Load older notifications</span>
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="p-2.5 border-t border-base-200 bg-base-200/40 text-center">
            <Link
              to={notificationsPageLink}
              onClick={() => setIsOpen(false)}
              className="text-xs font-semibold text-primary hover:underline block py-0.5"
            >
              View all notifications →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
